import React, { useState, useMemo, useEffect, useContext } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FiChevronDown, FiChevronRight, FiDownload } from "react-icons/fi";
import { PiRecordFill } from "react-icons/pi";
import SelectedCoachSlider from "./SelectedCoachSlider";
import AuthContext from "../../context/AuthContext.js";
import DownloadPDF from "../../utils/DownloadPDF.js";
import getCoachImage from "../../utils/coachImageHelper.js";

const LOCO_TYPES = new Set(["LOCO", "WAG12", "WAP5"]);

const TrainCoachSlider = ({ coachData, groupedTemperatureData, trainId, temperatureLogs, trainDetails }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCoachIndex, setSelectedCoachIndex] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imagesPerView, setImagesPerView] = useState(
    window.innerWidth < 768 ? 2 : 8
  );

  const { selectedSite, selectedSiteName } = useContext(AuthContext);

  const defaultCoachType = useMemo(() => {
    return (
      trainDetails?.Description ||
      trainDetails?.TypeOfCoaches ||
      trainDetails?.CoachType ||
      trainDetails?.TrainType ||
      coachData?.trainType ||
      temperatureLogs?.[0]?.Description ||
      temperatureLogs?.[0]?.CoachType ||
      ""
    );
  }, [trainDetails, coachData, temperatureLogs]);

  useEffect(() => {
    const handleResize = () => {
      setImagesPerView(window.innerWidth < 768 ? 2 : 8);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const allCoaches = useMemo(() => {
    if (coachData && Array.isArray(coachData.coaches) && coachData.coaches.length > 0) {
      return coachData.coaches
        .filter((coach) => coach.coachPosition !== null && coach.coachPosition !== undefined)
        .map((coach) => {
          let hasAlarm = false;
          let hasWarning = false;

          if (Array.isArray(coach.axles)) {
            hasAlarm = coach.axles.some(
              (axle) =>
                Array.isArray(axle.temperatures) &&
                axle.temperatures.some((temp) => temp?.hasAlarm === true)
            );
            hasWarning = coach.axles.some(
              (axle) =>
                Array.isArray(axle.temperatures) &&
                axle.temperatures.some((temp) => temp?.hasWarning === true)
            );
          }

          let label = coach.label || coach.coachPosition;

          return {
            type: coach.coachPosition || coach.type || "COACH",
            label:
              LOCO_TYPES.has(coach.coachPosition) || String(coach.coachPosition).toUpperCase().includes("LOCO")
                ? label
                : (String(label).startsWith("Coach") ? label : `Coach ${label}`),
            position: coach.coachPosition,
            coachNumber: coach.coachNumber,
            entryCount: coach.entryCount || 0,
            axles: Array.isArray(coach.axles) ? coach.axles : [],
            hasAlarm,
            hasWarning,
            averages: coach.averages || {},
            defaultCoachType,
            rawCoach: coach,
          };
        });
    }

    if (temperatureLogs && Array.isArray(temperatureLogs) && temperatureLogs.length > 0) {
      const coachMap = {};
      const order = [];

      temperatureLogs.forEach((log) => {
        const pos = log.CoachPosition || "COACH";
        const cNo = log.CoachNo || "";
        const key = `${pos}_${cNo}`;

        if (!coachMap[key]) {
          order.push(key);
          coachMap[key] = {
            type: pos,
            position: pos,
            coachNumber: cNo,
            label: pos.toUpperCase().includes("LOCO") || pos.toUpperCase().includes("WAG12")
              ? pos
              : `Coach ${pos}`,
            axles: [],
            hasAlarm: false,
            hasWarning: false,
            averages: {},
            defaultCoachType,
          };
        }

        const hasLogAlarm = Boolean(
          log.Ts1Alarm || log.Ts2Alarm || log.Ts3Alarm || log.Ts4Alarm || log.Ts5Alarm || log.Ts6Alarm
        );
        const hasLogWarning = Boolean(
          log.Ts1Warning || log.Ts2Warning || log.Ts3Warning || log.Ts4Warning || log.Ts5Warning || log.Ts6Warning
        );

        if (hasLogAlarm) coachMap[key].hasAlarm = true;
        if (hasLogWarning) coachMap[key].hasWarning = true;

        coachMap[key].axles.push({
          axleNo: log.AxleNo,
          temperatures: [
            { sensor: "Ts1", value: log.Ts1, hasAlarm: Boolean(log.Ts1Alarm), hasWarning: Boolean(log.Ts1Warning) },
            { sensor: "Ts2", value: log.Ts2, hasAlarm: Boolean(log.Ts2Alarm), hasWarning: Boolean(log.Ts2Warning) },
            { sensor: "Ts3", value: log.Ts3, hasAlarm: Boolean(log.Ts3Alarm), hasWarning: Boolean(log.Ts3Warning) },
            { sensor: "Ts4", value: log.Ts4, hasAlarm: Boolean(log.Ts4Alarm), hasWarning: Boolean(log.Ts4Warning) },
            { sensor: "Ts5", value: log.Ts5, hasAlarm: Boolean(log.Ts5Alarm), hasWarning: Boolean(log.Ts5Warning) },
            { sensor: "Ts6", value: log.Ts6, hasAlarm: Boolean(log.Ts6Alarm), hasWarning: Boolean(log.Ts6Warning) },
          ],
        });
      });

      return order.map((key) => coachMap[key]);
    }

    return [];
  }, [coachData, temperatureLogs, defaultCoachType]);

  useEffect(() => {
    setCurrentIndex(0);
    setSelectedCoachIndex(allCoaches.length > 0 ? 0 : null);
  }, [coachData, allCoaches.length]);

  const totalCoaches = allCoaches.length;
  const coachWidth = `calc((100% - ${(imagesPerView - 1) * 0.5}rem) / ${imagesPerView})`;

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - imagesPerView));
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev + imagesPerView >= totalCoaches ? 0 : prev + imagesPerView
    );
  };

  const visibleCoaches = useMemo(
    () => allCoaches.slice(currentIndex, currentIndex + imagesPerView),
    [currentIndex, allCoaches, imagesPerView]
  );

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleDownload = () => {
    const trainDetails = { TrainId: trainId };
    DownloadPDF(selectedSite, selectedSiteName, trainDetails, setIsLoading);
  };

  if (totalCoaches === 0) return null;

  return (
    <div className="relative w-full mx-auto bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-blue-900 text-white p-3 rounded-t-xl flex items-center justify-between">
        <div className="font-bold text-lg">Train Composition</div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            className="text-white hover:text-gray-300 transition"
            aria-label="Download"
            disabled={isLoading}
          >
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <FiDownload size={20} />
            )}
          </button>
          <button
            onClick={toggleCollapse}
            className="text-white hover:text-gray-300 transition"
            aria-label="Toggle Slider"
          >
            {isCollapsed ? <FiChevronRight size={20} /> : <FiChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Slider */}
      {!isCollapsed && (
        <div className="p-4 pt-3">
          <div className="flex items-center gap-2">
            {totalCoaches > imagesPerView && (
              <button
                onClick={handlePrev}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={currentIndex === 0}
              >
                <FaChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
            )}
            <div className="flex-1 overflow-hidden">
              <div className="flex transition-transform duration-500 ease-in-out gap-2">
                {visibleCoaches.map((coach, index) => {
                  const isSelected = selectedCoachIndex === currentIndex + index;
                  return (
                    <div
                      key={index}
                      className={`flex-shrink-0 flex flex-col justify-start items-center relative cursor-pointer hover:opacity-80 transition-opacity box-border p-1 ${isSelected
                          ? "border-[2px] border-blue-900 rounded-lg"
                          : "border border-transparent"
                        }`}
                      style={{
                        width: coachWidth,
                        minWidth: coachWidth,
                      }}
                      onClick={() => setSelectedCoachIndex(currentIndex + index)}
                    >
                      <div className="relative w-full h-24 flex items-center justify-center">
                        <PiRecordFill
                          className="absolute top-0 left-1/2 transform -translate-x-1/2 z-[2]"
                          size={18}
                          color={
                            coach.hasAlarm
                              ? "red"
                              : coach.hasWarning
                              ? "#DAA520"
                              : "green"
                          }
                        />
                        <img
                          src={getCoachImage(coach, defaultCoachType)}
                          alt={coach.label}
                          className="w-full h-full object-contain rounded"
                        />
                      </div>
                      <div className="text-center text-black text-sm md:text-base font-medium mt-1 hidden sm:block">
                        {coach.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {totalCoaches > imagesPerView && (
              <button
                onClick={handleNext}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={currentIndex + imagesPerView >= totalCoaches}
              >
                <FaChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            )}
          </div>
          <div className="text-center text-sm text-gray-600 mb-2">
            {`Showing ${currentIndex + 1}-${Math.min(
              currentIndex + imagesPerView,
              totalCoaches
            )} of ${totalCoaches}`}
          </div>
          {selectedCoachIndex !== null && (
            <div className="-mt-4">
              <SelectedCoachSlider
                selectedCoachIndex={selectedCoachIndex}
                allCoaches={allCoaches}
                groupedTemperatureData={groupedTemperatureData}
                defaultCoachType={defaultCoachType}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainCoachSlider;